/**
 * Le righe che il Gateway ZDHC non ha accettato, tradotte nei **prodotti
 * locali** che le hanno prodotte.
 *
 * L'api spinge le righe dell'inventario una a una e raccoglie i 422 per
 * **indice di riga** (`ZdhcInventoryService::syncLines`): non conosce i nostri
 * prodotti, quindi non puo' nominarli. Chi ha costruito l'invio sa da quale
 * prodotto viene ogni riga, e questa e' la traduzione — la stessa che
 * `duplicateReportGroups` fa per i duplicati, sulla stessa convenzione di
 * indici: `normalizedLines()` mappa il payload con `array_map`, che preserva le
 * chiavi, quindi l'indice che torna e' l'indice dell'array inviato.
 *
 * Sta in un modulo e non dentro il modale per due ragioni: e' aritmetica sugli
 * indici, cioe' la cosa che sbaglia in silenzio — un fuori-range restituisce un
 * nome sbagliato invece di rompersi, e a schermo nessuno se ne accorge — e sul
 * client non c'e' un runner di test, quindi cio' che va provato deve essere
 * importabile da un harness.
 */

/**
 * I messaggi di un campo, ripuliti di cio' che non e' testo.
 *
 * Il Gateway dichiara `errors` come oggetto di array di stringhe, ma qui
 * arriviamo dopo due salti di rete e una deserializzazione: un valore singolo
 * invece dell'array, o un `null` in mezzo, non deve diventare "null" stampato
 * in un dialog. Cio' che non e' una stringa con del testo non e' un messaggio.
 */
const messagesOf = (value) =>
{
    const list = Array.isArray(value) ? value : [value];

    return list
        .filter( (message) => typeof message === 'string' && message.trim() !== '')
        .map( (message) => message.trim());
};

/**
 * @param {Array} linesFailed le righe come le manda l'api:
 *        `{ index: number, line: object, errors: { campo: string[] } }`
 * @param {Array} lineProducts i prodotti locali, nell'ordine in cui le righe
 *        sono state costruite
 * @returns {Array} `{ name, referenceId, productName, formulatorName, reasons }`
 *          — `name` porta `null` dove il nome non si sa, e a decidere come
 *          scriverlo e' chi rende il testo, che ha le traduzioni; `reasons` e'
 *          `{ field, messages }[]`, il campo rifiutato e cosa ne ha detto il
 *          Gateway
 */
export const rejectedLinesFrom = (linesFailed, lineProducts) =>
{
    if(!Array.isArray(linesFailed)) return [];

    const products = Array.isArray(lineProducts) ? lineProducts : [];

    return linesFailed.map( (failure) =>
    {
        const line = failure?.line ?? {};
        const errors = failure?.errors;
        /*
         * Un indice che non e' un intero non entra in un array: `products['x']`
         * e' `undefined`, ma `products[1.5]` pure, e la differenza fra "nessun
         * prodotto a quell'indice" e "indice che non e' un indice" qui non
         * cambia l'esito — cio' che conta e' che non si finisca a leggere una
         * proprieta' a caso dell'array (`products['length']` sarebbe un numero).
         */
        const index = Number.isInteger(failure?.index) ? failure.index : null;

        return {
            // `?? null` e non `?? ''`: un indice che non trova il suo prodotto
            // e' un nome **che non sappiamo**, e va detto come tale invece di
            // diventare una stringa vuota in mezzo all'elenco.
            name: (index === null ? null : products[index]?.name) ?? null,
            // L'identita' con cui la riga e' partita, non quella del prodotto
            // locale: e' il valore che il Gateway ha rifiutato, ed e' quello che
            // l'utente deve poter leggere per capire cosa correggere.
            referenceId: line.product_reference_id ?? null,
            productName: line.product_name ?? null,
            formulatorName: line.formulator_name ?? null,
            /*
             * Un campo per motivo, nell'ordine in cui il Gateway li elenca.
             * `Object.entries` su un non-oggetto solleverebbe solo per `null` e
             * `undefined`, ma su una stringa darebbe le sue lettere: il
             * controllo di tipo e' esplicito perche' un `errors` malformato
             * produca un elenco vuoto, non un elenco di caratteri.
             */
            reasons: (errors !== null && typeof errors === 'object' && !Array.isArray(errors)
                ? Object.entries(errors)
                : [])
                .map( ([field, value]) => ({ field, messages: messagesOf(value) }))
        };
    });
};

export default rejectedLinesFrom;
